import type { EnvironmentClass } from "../api/types";
import { StatePill, type PillShape, type PillTone } from "./StatePill";

/**
 * Environment as a facet on the things you deploy (Host/Deploy UX Phase 4,
 * #526) — the review's finding was that Environment lived only in a separate
 * `/environments` CRUD section, "configured here but observed nowhere." This
 * is the ONE tone/shape table that drives every environment-flavored surface
 * in the app (A-5: placements, deployment detail, hosts, and the admin
 * environments list all import this rather than each inventing their own
 * mapping) so staging vs. production reads identically everywhere an
 * operator meets it. Production is the class that most needs to stand out —
 * it's the one that (depending on policy) requires promotion and is where a
 * mistake actually hurts — so it alone gets the warning tone and a distinct
 * triangle glyph; staging/preview read as cooler "info"; development is the
 * quiet, no-consequence default. An unrecognized class (version skew, A-3)
 * falls back to a neutral pill carrying the server's own string rather than
 * guessing at meaning.
 */
const ENV_CLASS_META: Record<EnvironmentClass, { label: string; tone: PillTone; shape: PillShape }> = {
  development: { label: "Development", tone: "neutral", shape: "ring" },
  preview: { label: "Preview", tone: "info", shape: "bars" },
  staging: { label: "Staging", tone: "info", shape: "dot" },
  production: { label: "Production", tone: "warning", shape: "triangle" },
};

export interface EnvironmentBadgeProps {
  /** The environment's operator-chosen display name ("production", "prod-eu",
   * "pr-1234"). When present, THIS is what renders — an environment renamed
   * away from its class must still show its real name, with `envClass` only
   * driving the tone/glyph, never the label. Omit for a class-only badge
   * (e.g. the admin environments list, which already has its own Name
   * column) — the class's own label stands in for text instead. */
  name?: string;
  /** The environment's policy class, when resolvable. Absent or unrecognized
   * falls back to a neutral pill (never a guessed class — A-10). */
  envClass?: string;
}

/**
 * Renders an environment as a small, class-aware pill. Renders NOTHING when
 * both `name` and `envClass` are absent — there is no "no environment"
 * placeholder pill baked in here; callers own their own honest empty text
 * (e.g. "—", matching every other optional `DataTable` column in this app)
 * rather than this component fabricating an environment that isn't there
 * (A-10). A service/placement/host genuinely has no environment sometimes
 * (a BYO node never attached to one) — that must read as neutral absence,
 * never as a guessed "production".
 */
export function EnvironmentBadge({ name, envClass }: EnvironmentBadgeProps) {
  if (!name && !envClass) return null;
  const meta = envClass
    ? ENV_CLASS_META[envClass as EnvironmentClass] ?? { label: envClass, tone: "neutral" as const, shape: "ring" as const }
    : { label: "Unknown class", tone: "neutral" as const, shape: "ring" as const };
  return <StatePill label={name || meta.label} tone={meta.tone} shape={meta.shape} />;
}
